import { apiClient, getAuthHeaders } from './api';

export interface CartItem {
  id: string;
  product_id: string;
  product_name: string;
  product_description: string;
  epicor_part_number: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  created_at: string;
  updated_at: string;
  // Discount fields
  discountPercent?: number;
  discountAmount?: number;
  discountedUnitPrice?: number;
  discountSource?: string;
  originalTotalPrice?: number;
  totalDiscountAmount?: number;
  hasDiscount?: boolean;
}

export interface CartResponse {
  user_id: string;
  items: CartItem[];
  total_items: number;
  total_amount: number;
}

export interface AddToCartRequest {
  product_id: string;
  quantity: number;
}

export interface UpdateCartItemRequest {
  quantity: number;
}

class CartService {
  /**
   * Map API response fields from snake_case to camelCase
   */
  private mapCartItemFields(apiItem: any): CartItem {
    return {
      ...apiItem,
      // Map discount fields from snake_case to camelCase
      discountPercent: apiItem.discount_percent,
      discountAmount: apiItem.discount_amount,
      discountedUnitPrice: apiItem.discounted_unit_price,
      discountSource: apiItem.discount_source,
      originalTotalPrice: apiItem.original_total_price,
      totalDiscountAmount: apiItem.discount_amount ? apiItem.discount_amount * apiItem.quantity : 0,
      hasDiscount: apiItem.has_discount,
    };
  }

  /**
   * Get current user's cart items
   */
  async getCartItems(): Promise<CartResponse> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.get<any>('/cart/', headers);
      // Map cart items to include camelCase fields
      return {
        ...response,
        items: response.items.map((item: any) => this.mapCartItemFields(item))
      };
    } catch (error) {
      console.error('Get cart items error:', error);
      throw error;
    }
  }

  /**
   * Add item to cart
   */
  async addToCart(item: AddToCartRequest): Promise<CartItem> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.post<any>('/cart/items', item, headers);
      return this.mapCartItemFields(response);
    } catch (error) {
      console.error('Add to cart error:', error);
      throw error;
    }
  }

  /**
   * Update cart item quantity
   */
  async updateCartItem(productId: string, data: UpdateCartItemRequest): Promise<CartItem> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.put<any>(`/cart/items/${productId}`, data, headers);
      return this.mapCartItemFields(response);
    } catch (error) {
      console.error('Update cart item error:', error);
      throw error;
    }
  }

  /**
   * Remove item from cart
   */
  async removeFromCart(productId: string): Promise<{ message: string }> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.delete<{ message: string }>(`/cart/items/${productId}`, headers);
      return response;
    } catch (error) {
      console.error('Remove from cart error:', error);
      throw error;
    }
  }

  /**
   * Clear all items from cart
   */
  async clearCart(): Promise<{ message: string }> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.delete<{ message: string }>('/cart/clear', headers);
      return response;
    } catch (error) {
      console.error('Clear cart error:', error);
      throw error;
    }
  }
}

export const cartService = new CartService();